import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const DemandsPanel = ({ demands = [], loading = false, onApproveDemand, onRejectDemand }) => {
  const [selectedDemands, setSelectedDemands] = useState([]);
  const navigate = useNavigate();

  const handleSelectDemand = (demandId) => {
    setSelectedDemands(prev => 
      prev?.includes(demandId) 
        ? prev?.filter(id => id !== demandId)
        : [...prev, demandId]
    );
  };

  const handleBulkApprove = () => {
    selectedDemands?.forEach(id => onApproveDemand(id));
    setSelectedDemands([]);
  };

  const handleBulkReject = () => {
    selectedDemands?.forEach(id => onRejectDemand(id));
    setSelectedDemands([]);
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'Alta':
        return 'bg-error/10 text-error border-error/20';
      case 'Média':
        return 'bg-warning/10 text-warning border-warning/20';
      case 'Baixa':
        return 'bg-success/10 text-success border-success/20';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'Alta':
        return 'AlertTriangle';
      case 'Média':
        return 'AlertCircle';
      case 'Baixa':
        return 'Info';
      default:
        return 'Circle';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'Manutenção':
        return 'Wrench';
      case 'Instalação':
        return 'Plus';
      case 'Suporte':
        return 'HelpCircle';
      case 'Consultoria':
        return 'MessageSquare';
      default:
        return 'FileText';
    }
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg">
        <div className="p-6 border-b border-border">
          <div className="w-40 h-6 bg-muted rounded animate-pulse"></div>
        </div>
        <div className="p-6 space-y-4">
          {[...Array(4)]?.map((_, i) => (
            <div key={i} className="flex items-start space-x-4 animate-pulse">
              <div className="w-4 h-4 bg-muted rounded mt-1"></div>
              <div className="flex-1 space-y-2">
                <div className="w-3/4 h-4 bg-muted rounded"></div>
                <div className="w-1/2 h-3 bg-muted rounded"></div>
                <div className="w-1/4 h-3 bg-muted rounded"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground">Demandas Pendentes</h3>
          <div className="flex items-center space-x-2">
            <span className="text-sm text-muted-foreground">
              {demands?.filter(d => d?.status === 'Pendente')?.length} pendentes
            </span>
            <Button
              variant="outline"
              size="sm"
              iconName="Eye"
              onClick={() => navigate('/client-portal')}
            >
              Ver Todas
            </Button>
          </div>
        </div>

        {selectedDemands?.length > 0 && (
          <div className="flex items-center space-x-2 p-3 bg-muted/50 rounded-lg">
            <span className="text-sm text-foreground">
              {selectedDemands?.length} selecionadas
            </span>
            <Button
              variant="success"
              size="sm"
              iconName="Check"
              onClick={handleBulkApprove}
            >
              Aprovar
            </Button>
            <Button
              variant="destructive"
              size="sm"
              iconName="X"
              onClick={handleBulkReject}
            >
              Rejeitar
            </Button>
          </div>
        )}
      </div>
      <div className="max-h-96 overflow-y-auto">
        {demands?.length === 0 ? (
          <div className="p-8 text-center">
            <Icon name="CheckCircle" size={48} className="mx-auto mb-4 text-success opacity-50" />
            <p className="text-muted-foreground">Todas as demandas foram processadas</p>
          </div>
        ) : (
          <div className="p-6 space-y-4">
            {demands?.map((demand) => (
              <div 
                key={demand?.id} 
                className={`border border-border rounded-lg p-4 hover:bg-muted/30 transition-colors ${
                  selectedDemands?.includes(demand?.id) ? 'bg-primary/5 border-primary/20' : ''
                }`}
              >
                <div className="flex items-start space-x-3">
                  <input
                    type="checkbox"
                    checked={selectedDemands?.includes(demand?.id)}
                    onChange={() => handleSelectDemand(demand?.id)}
                    className="mt-1 w-4 h-4 text-primary border-border rounded focus:ring-primary"
                  />
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2 mb-2">
                      <div className="w-6 h-6 bg-muted rounded flex items-center justify-center">
                        <Icon name={getTypeIcon(demand?.type)} size={14} className="text-muted-foreground" />
                      </div>
                      <h4 className="font-medium text-foreground truncate">{demand?.title}</h4>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${getPriorityColor(demand?.priority)}`}>
                        <Icon name={getPriorityIcon(demand?.priority)} size={12} className="mr-1" />
                        {demand?.priority}
                      </span>
                    </div>

                    <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                      {demand?.description}
                    </p>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                        <div className="flex items-center space-x-1">
                          <Icon name="Building" size={12} />
                          <span>{demand?.client}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Icon name="Calendar" size={12} />
                          <span>{demand?.createdAt}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Icon name="Tag" size={12} />
                          <span>{demand?.type}</span>
                        </div>
                      </div>

                      <div className="flex items-center space-x-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          iconName="Eye"
                          onClick={() => navigate('/client-portal')}
                        >
                          Ver
                        </Button>
                        <Button
                          variant="success"
                          size="sm"
                          iconName="Check"
                          onClick={() => onApproveDemand(demand?.id)}
                        >
                          Aprovar
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          iconName="X"
                          onClick={() => onRejectDemand(demand?.id)}
                        >
                          Rejeitar
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DemandsPanel;